class LilliputProductTabs extends HTMLElement {
  connectedCallback() {
    this.tabs = this.querySelectorAll('[data-tab]');
    this.panels = this.querySelectorAll('[data-panel]');
    this.specRoot = this.querySelector('[data-product-specs]');
    this.baseSpecValues = this.captureBaseSpecValues();
    this.variantSpecs = this.parseVariantSpecs();

    // Wire tabs first so a specs error cannot leave the nav dead.
    this.tabs.forEach((tab) => {
      tab.addEventListener('click', () => this.activate(tab.dataset.tab));
    });

    try {
      this.applyVariantSpecs(this.dataset.selectedSku);
    } catch (error) {
      console.warn('[lilliput-product-tabs] applyVariantSpecs failed', error);
    }

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
        try {
          this.applyVariantSpecs(event.data?.variant?.sku);
        } catch (error) {
          console.warn('[lilliput-product-tabs] applyVariantSpecs failed', error);
        }
      });
    }
  }

  disconnectedCallback() {
    if (this.variantChangeUnsubscriber) {
      this.variantChangeUnsubscriber();
    }
  }

  parseVariantSpecs() {
    try {
      return JSON.parse(this.dataset.variantSpecs || '[]');
    } catch {
      return [];
    }
  }

  captureBaseSpecValues() {
    const values = new Map();
    if (!this.specRoot) return values;

    this.specRoot.querySelectorAll('[data-spec-row]').forEach((row) => {
      const label = row.dataset.specLabel;
      const cell = row.querySelector('[data-spec-value]');
      if (label && cell) values.set(label, cell.innerHTML);
    });

    return values;
  }

  findSpecRow(label) {
    if (!this.specRoot) return null;
    return Array.from(this.specRoot.querySelectorAll('[data-spec-row]')).find(
      (row) => row.dataset.specLabel === label
    );
  }

  flattenSpecOverrides(overrides) {
    const flat = {};
    if (!overrides || typeof overrides !== 'object') return flat;

    for (const [key, value] of Object.entries(overrides)) {
      if (value && typeof value === 'object' && !Array.isArray(value)) {
        Object.assign(flat, this.flattenSpecOverrides(value));
      } else if (Array.isArray(value)) {
        flat[key] = value.join(', ');
      } else {
        flat[key] = String(value);
      }
    }

    return flat;
  }

  applyVariantSpecs(variantSku) {
    if (!this.specRoot || !this.baseSpecValues.size) return;

    this.baseSpecValues.forEach((html, label) => {
      const cell = this.findSpecRow(label)?.querySelector('[data-spec-value]');
      if (cell) cell.innerHTML = html;
    });

    if (!variantSku) return;

    const variantSpec = this.variantSpecs.find((entry) => entry.id === variantSku);
    if (!variantSpec?.specOverrides) return;

    const overrides = this.flattenSpecOverrides(variantSpec.specOverrides);
    for (const [label, value] of Object.entries(overrides)) {
      const cell = this.findSpecRow(label)?.querySelector('[data-spec-value]');
      if (cell) cell.textContent = value;
    }
  }

  activate(name) {
    this.tabs.forEach((tab) => {
      const active = tab.dataset.tab === name;
      tab.classList.toggle('is-active', active);
      tab.setAttribute('aria-selected', active ? 'true' : 'false');
    });
    this.panels.forEach((panel) => {
      panel.hidden = panel.dataset.panel !== name;
    });
  }
}

if (!customElements.get('lilliput-product-tabs')) {
  customElements.define('lilliput-product-tabs', LilliputProductTabs);
}
